import { message } from "antd";
import { BusinessResponse, ContentTopic } from "api/config/chalice-types";
import { POSTS_PAGE_ROUTE } from "pages/Posts/PostsPage";
import { POST_SCREEN_TAB } from "pages/Posts/utils/useGetAllowedScreens";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "store";
import { trackEvent } from "utils/eventTracking/trackEvents";
import { saveTopics } from "./topicActionUtils"; // Correct import path

// --- Define Prop Types --- //
export interface CommonActionProps {
  displayTopics: ContentTopic[];
  setDisplayTopics: Dispatch<SetStateAction<ContentTopic[]>>;
  // serverTopics removed as it's handled by saveTopics
  currentBusiness: BusinessResponse | null;
  dispatch: AppDispatch;
  minimumTopicsMessage: () => void;
}

export interface UseTopicActionsProps extends CommonActionProps {
  closeModal: () => void;
  navigate: ReturnType<typeof useNavigate>;
}

// --- Define Return Type --- //
export interface UseTopicActionsReturn {
  handleUpdateTopics: (
    newTopicData: Partial<ContentTopic> & { category_id?: string | null },
    updateIndex?: number
  ) => Promise<void>;
  handleDeleteTopic: (topicToDelete: ContentTopic, topicIndex: number) => void;
  handleToggleEnabled: (topicIndex: number) => void;
  handleGeneratePostsForTopic: () => void;
}

// --- Hook: useTopicActions --- //
export const useTopicActions = ({
  displayTopics,
  setDisplayTopics,
  currentBusiness,
  dispatch,
  minimumTopicsMessage,
  closeModal,
  navigate,
}: UseTopicActionsProps): UseTopicActionsReturn => {
  const saveTopicsWrapper = (newT: ContentTopic[], origT: ContentTopic[]) =>
    saveTopics(newT, origT, setDisplayTopics, dispatch, currentBusiness?.id);

  const handleUpdateTopics = useCallback(
    async (
      newTopicData: Partial<ContentTopic> & { category_id?: string | null },
      updateIndex?: number
    ) => {
      const finalTopicData: ContentTopic = {
        title: newTopicData.title || "",
        body: newTopicData.body || "",
        enabled: newTopicData.enabled ?? true,
        category_id: newTopicData.category_id,
      };

      if (!finalTopicData.title || !finalTopicData.body) {
        message.error("Topic title and body cannot be empty");
        return;
      }

      const originalTopics = [...displayTopics];
      let newTopics: ContentTopic[];

      if (updateIndex !== undefined) {
        newTopics = originalTopics.map((topic, index) =>
          index === updateIndex ? finalTopicData : topic
        );
      } else {
        newTopics = [...originalTopics, finalTopicData];
      }

      try {
        await saveTopicsWrapper(newTopics, originalTopics);
      } catch {
        message.error(
          `Failed to ${updateIndex === undefined ? "add" : "update"} topic`
        );
      }
    },
    [displayTopics, saveTopicsWrapper]
  );

  const handleDeleteTopic = useCallback(
    (topicToDelete: ContentTopic, topicIndex: number) => {
      if (topicIndex < 0 || topicIndex >= displayTopics.length) return;

      const originalTopics = [...displayTopics];
      const currentEnabledCount = displayTopics.filter((t) => t.enabled).length;

      if (topicToDelete.enabled && currentEnabledCount <= 1) {
        minimumTopicsMessage();
      } else {
        const newTopics = originalTopics.filter(
          (_, index) => index !== topicIndex
        );

        saveTopicsWrapper(newTopics, originalTopics)
          .then(() => {
            trackEvent("updated_topics", { action: "delete" });
            // message.success("Topic deleted");
          })
          .catch(() => {});
      }
    },
    [displayTopics, saveTopicsWrapper, minimumTopicsMessage]
  );

  const handleToggleEnabled = useCallback(
    (topicIndex: number) => {
      if (topicIndex < 0 || topicIndex >= displayTopics.length) return;

      const originalTopics = [...displayTopics];
      const topicToToggle = originalTopics[topicIndex];
      const newEnabled = !topicToToggle.enabled;
      const currentEnabledCount = displayTopics.filter((t) => t.enabled).length;

      if (!newEnabled && currentEnabledCount <= 1) {
        minimumTopicsMessage();
      } else {
        const newTopics = originalTopics.map((topic, index) =>
          index === topicIndex ? { ...topic, enabled: newEnabled } : topic
        );
        saveTopicsWrapper(newTopics, originalTopics).catch(() => {});
      }
    },
    [displayTopics, saveTopicsWrapper, minimumTopicsMessage]
  );

  const handleGeneratePostsForTopic = useCallback(async () => {
    message.info({
      key: "view-generated-posts",
      content: "Generating posts, you will be redirected shortly",
      duration: 3,
    });

    setTimeout(() => {
      navigate({
        pathname: POSTS_PAGE_ROUTE,
        search: `${POST_SCREEN_TAB}=generated`,
        hash: "#bottom",
      });
    }, 1500);

    closeModal();
  }, [navigate, closeModal]);

  return {
    handleUpdateTopics,
    handleDeleteTopic,
    handleToggleEnabled,
    handleGeneratePostsForTopic,
  };
};
